import { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { MediaWeeklyReport as WeeklyReport } from '@/common/api/params/mediaDriver'
import {
  analysisTypeHistoryPath,
  analysisTypeLabel
} from '@/common/app/modules/media/mediaAnalysis'
import { articleListPath, mediaAnalysisResultPath } from '@/common/app/path'
import { dateStr } from '@/common/form/date'
import { ArticleCreationSource } from '@/common/model/media/article'
import { MediaAnalysisType } from '@/common/model/media/mediaAnalysis'

import TextButton from '@/components/view/atoms/button/TextButton'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

// 更新リスト（リライト・記事作成・分析）
const UpdateList = <T,>({
  title,
  items,
  renderItem,
  emptyText,
  linkNode,
  footer
}: {
  title: string
  items: T[]
  renderItem: (item: T) => ReactNode
  emptyText: string
  linkNode?: ReactNode
  footer?: ReactNode
}) => (
  <Area>
    <Heading subText={`${items.length} 件`} rightNode={linkNode}>
      {title}
    </Heading>
    <AreaContent>
      {items.length === 0 ? (
        <Text size={12} color={TextColor.SECONDARY} align='center'>
          {emptyText}
        </Text>
      ) : (
        <div className='border-secondary flex max-h-[280px] flex-col gap-2.5 overflow-y-auto rounded-lg border'>
          {items.map((item, i) => (
            <div
              key={i}
              className='border-secondary flex items-center gap-4 border-b px-2 py-2 last:border-none'
            >
              {renderItem(item)}
            </div>
          ))}
        </div>
      )}
      {footer}
    </AreaContent>
  </Area>
)

interface Props {
  siteId: number
  weeklyReport: WeeklyReport
}

// 今週の報告（サマリー + 更新詳細）
const MediaWeeklyReport = ({ siteId, weeklyReport }: Props) => {
  const navigate = useNavigate()
  const summary = weeklyReport.summary
  const updates = weeklyReport.updates

  return (
    <>
      <SummaryHeading>今週の報告</SummaryHeading>
      <Area bottomMargin={16}>
        <SummaryItemList>
          <SummaryItem>
            <Text weight='bold'>今週のリライト</Text>
            <ItemAlign>
              <Text weight='bold' size={24} color={TextColor.SUCCESS}>
                {summary.rewrite_count || 0}
              </Text>
              <Text size={14}>件</Text>
            </ItemAlign>
          </SummaryItem>
          <SummaryItem>
            <Text weight='bold'>今週の記事作成</Text>
            <ItemAlign>
              <Text weight='bold' size={24} color={TextColor.SUCCESS}>
                {summary.article_count || 0}
              </Text>
              <Text size={14}>件</Text>
            </ItemAlign>
          </SummaryItem>
          <SummaryItem>
            <Text weight='bold'>今週の分析実行</Text>
            <ItemAlign>
              <Text weight='bold' size={24}>
                {summary.analysis_count || 0}
              </Text>
              <Text size={14}>件</Text>
            </ItemAlign>
          </SummaryItem>
          <SummaryItem>
            <Text weight='bold'>不足記事</Text>
            <ItemAlign>
              <Text weight='bold' size={24} color={TextColor.CRITICAL}>
                {summary.missing_article_count || 0}
              </Text>
              <Text size={14}>件</Text>
            </ItemAlign>
          </SummaryItem>
        </SummaryItemList>
      </Area>

      {/* 今週の更新詳細 */}
      <div className='mb-4 grid grid-cols-1 gap-3.5 lg:grid-cols-3'>
        <UpdateList
          title='記事リライト'
          items={updates.rewrites}
          emptyText='今週のリライトはありません'
          renderItem={(r) => (
            <>
              <div className='w-10 shrink-0 pt-0.5'>
                <Text size={10} color={TextColor.SECONDARY}>
                  {r.created_at ? dateStr(r.created_at).slice(5) : '-'}
                </Text>
              </div>
              <div className='min-w-0 flex-1'>
                <Text size={12} weight='bold' truncate>
                  {r.title || r.page_id}
                </Text>
                <Text size={10} color={TextColor.SECONDARY} truncate>
                  v{r.version_number}
                </Text>
              </div>
            </>
          )}
          linkNode={
            <TextButton
              arrow
              link={articleListPath(siteId, { creationSource: ArticleCreationSource.EDIT })}
            >
              リライト一覧
            </TextButton>
          }
        />

        <UpdateList
          title='記事作成'
          items={updates.articles ?? []}
          emptyText='今週の記事作成はありません'
          linkNode={
            <TextButton arrow link={articleListPath(siteId)}>
              記事一覧
            </TextButton>
          }
          renderItem={(r) => (
            <>
              <div className='w-10 shrink-0 pt-0.5'>
                <Text size={10} color={TextColor.SECONDARY}>
                  {r.created_at ? dateStr(r.created_at).slice(5) : '-'}
                </Text>
              </div>
              <div className='min-w-0 flex-1'>
                <Text size={12} weight='bold' truncate>
                  {r.title || '（タイトル未設定）'}
                </Text>
                <Text size={10} color={TextColor.SECONDARY} truncate>
                  {analysisTypeLabel(r.type as MediaAnalysisType)}
                </Text>
              </div>
            </>
          )}
        />

        <UpdateList
          title='分析'
          items={updates.analyses ?? []}
          emptyText='今週の分析実行はありません'
          renderItem={(r) => {
            const linkPath = mediaAnalysisResultPath(siteId, r.analysis_type, r.id)
            return (
              <>
                <Text size={12}>{r.created_at ? dateStr(r.created_at).slice(5) : '-'}</Text>
                <ItemAlign direction='vertical' gapY={4} remnant>
                  <Text weight='bold' truncate>
                    {analysisTypeLabel(r.analysis_type)}
                  </Text>
                  <Text size={12} color={TextColor.SECONDARY} truncate>
                    {r.summary || '-'}
                  </Text>
                </ItemAlign>
                {linkPath && (
                  <div className='self-center'>
                    <TextButton link={linkPath} arrow>
                      履歴
                    </TextButton>
                  </div>
                )}
              </>
            )
          }}
        />
      </div>
    </>
  )
}

export default MediaWeeklyReport
